<template>
  <div class="cashflow-container">
    <!-- Header Section -->
    <div class="cashflow-header">
      <div class="header-content">
        <div class="header-text">
          <h1 class="cashflow-title">Raport Cash Flow</h1>
          <p class="cashflow-subtitle">Analiză încasări, plăți și solduri bancare</p>
        </div>
        <div class="header-actions">
          <v-btn
            color="primary"
            prepend-icon="mdi-refresh"
            variant="text"
            @click="getCashFlow(timeFrame, days)"
          >
            Actualizează
          </v-btn>
          <v-btn
            color="primary"
            prepend-icon="mdi-download"
            variant="outlined"
          >
            Export
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Modern Tabs -->
    <v-card class="tabs-card" elevation="2">
      <v-tabs v-model="activeTab" bg-color="white" color="primary" grow>
        <v-tab value="predictie">
          <v-icon start>mdi-chart-line</v-icon>
          Predicție încasări/plăți
        </v-tab>
        <v-tab value="banci">
          <v-icon start>mdi-bank</v-icon>
          Conturi Bănci
        </v-tab>
      </v-tabs>
    </v-card>

    <!-- Predicție Tab Content -->
    <div v-if="activeTab === 'predictie' && loadingFacturi === false">
      <!-- Days Selector Card -->
      <v-card class="control-card" elevation="2">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="6">
              <div class="control-label">
                <v-icon color="primary" class="mr-2">mdi-calendar-range</v-icon>
                Pentru câte zile se afișează date în viitor?
              </div>
            </v-col>
            <v-col cols="12" md="2">
              <DxNumberBox :value="days" :min="0" :show-spin-buttons="true" @value-changed="updateRange" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Chart Card -->
      <v-card class="chart-card" elevation="2">
        <v-card-title class="card-title">
          <v-icon color="primary" size="24" class="mr-2">mdi-chart-bar</v-icon>
          <span>Predicție încasări/plăți</span>
        </v-card-title>
        <v-card-text>
          <DxChart :data-source="dataSource">
            <DxCommonSeriesSettings argument-field="state" type="stackedbar" />
            <DxValueAxis position="right">
              <DxTitle text="RON" />
            </DxValueAxis>
            <DxSeries name="De Incasat" value-field="plus" color="#43A047" />
            <DxSeries name="De Achitat" value-field="minus" color="#D32F2F" />
            <DxSeries name="Total" value-field="total" type="spline" color="#6b71c3" />
            <DxLegend horizontal-alignment="center" item-text-position="top" vertical-alignment="bottom" />
            <DxTooltip :enabled="true" :customize-tooltip="customizeTooltip" zIndex="5000" />
            <DxExport :enabled="true" />
          </DxChart>
        </v-card-text>
      </v-card>

      <!-- Time Frame Selector -->
      <v-card class="timeframe-card" elevation="2">
        <v-card-text>
          <v-chip-group v-model="timeFrame" selected-class="text-primary" mandatory>
            <v-chip value="1d">1D</v-chip>
            <v-chip value="1w">1W</v-chip>
            <v-chip value="1m">1M</v-chip>
            <v-chip value="6m">6M</v-chip>
            <v-chip value="1y">1Y</v-chip>
            <v-chip value="5y">5Y</v-chip>
            <v-chip value="max">MAX</v-chip>
          </v-chip-group>
        </v-card-text>
      </v-card>

      <!-- Summary Stats Cards -->
      <v-row class="stats-row">
        <v-col cols="12" md="4">
          <v-card class="stat-card stat-card-plus" elevation="2">
            <div class="stat-icon-wrapper">
              <v-icon color="#43A047" size="32">mdi-arrow-up-bold</v-icon>
            </div>
            <div class="stat-content">
              <div class="stat-label">Total de încasat</div>
              <div class="stat-value plus_color">
                {{ totalDeIncasat.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} RON
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="stat-card stat-card-minus" elevation="2">
            <div class="stat-icon-wrapper">
              <v-icon color="#D32F2F" size="32">mdi-arrow-down-bold</v-icon>
            </div>
            <div class="stat-content">
              <div class="stat-label">Total de achitat</div>
              <div class="stat-value minus_color">
                {{ (-totalDeAchitat).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} RON
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="stat-card stat-card-total" elevation="2">
            <div class="stat-icon-wrapper">
              <v-icon color="#6b71c3" size="32">mdi-calculator</v-icon>
            </div>
            <div class="stat-content">
              <div class="stat-label">Total predicție</div>
              <div class="stat-value">
                <span v-if="total > 0.01" class="plus_color">
                  {{ total.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} RON
                </span>
                <span v-else-if="total < -0.01" class="minus_color">
                  {{ total.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} RON
                </span>
                <span v-else>0 RON</span>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Top Partners Grid -->
      <v-row class="partners-row">
        <v-col cols="12" md="6">
          <v-card class="partners-card" elevation="2">
            <v-card-title class="card-title">
              <v-icon color="primary" size="24" class="mr-2">mdi-arrow-up-bold-circle</v-icon>
              <span>Top sume de încasat după partener</span>
            </v-card-title>
            <v-card-text class="pa-0">
              <DxDataGrid :data-source="topNeincasat" :allowColumnReordering="true" :columns="['Nume', 'CUI', 'Sold']">
                <DxSorting mode="none" />
                <DxFilterRow :visible="true" />
              </DxDataGrid>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="partners-card" elevation="2">
            <v-card-title class="card-title">
              <v-icon color="primary" size="24" class="mr-2">mdi-arrow-down-bold-circle</v-icon>
              <span>Top sume de achitat după partener</span>
            </v-card-title>
            <v-card-text class="pa-0">
              <DxDataGrid :data-source="topNeachitat" :allowColumnReordering="true" :columns="['Nume', 'CUI', 'Sold']">
                <DxSorting mode="none" />
                <DxFilterRow :visible="true" />
              </DxDataGrid>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Loading Indicator -->
    <v-row v-if="(activeTab === 'predictie' && loadingFacturi === true) || (activeTab === 'banci' && loadingConturiBanci === true)">
      <v-col cols="12" class="text-center py-16">
        <DxLoadIndicator id="large-indicator" :height="60" :width="60" />
      </v-col>
    </v-row>

    <!-- Banci Tab Content -->
    <div v-if="activeTab === 'banci' && loadingConturiBanci === false">
      <v-row class="banks-row">
        <v-col cols="12" md="6">
          <v-card class="bank-card" elevation="2">
            <v-card-title class="card-title">
              <v-icon color="success" size="24" class="mr-2">mdi-plus-circle</v-icon>
              <span>Debit</span>
            </v-card-title>
            <v-card-text class="pa-2">
              <DxDataGrid :allowColumnReordering="true" :data-source="conturiBanci.filter(cb => cb.Final >= 0)"
                :allowColumnResizing="true" :columnAutoWidth="false" :columnMinWidth="50"
                :columnResizingMode="'nextColumn'" :show-borders="true" :syncLookupFilterValues="false"
                @exporting="onExporting" :filter-sync-enabled="true" style="height: 600px">
                <DxExportChart :enabled="true" :formats="['pdf', 'xlsx']" :allow-export-selected-data="true" />
                <DxFilterRow :visible="true" />
                <DxScrolling mode="virtual" />
                <DxGrouping :context-menu-enabled="false" :autoExpandAll="false" />
                <DxGroupPanel :visible="true" :allow-column-dragging="false" />
                <DxLoadPanel :enabled="true" :visible="loadingConturiBanci" />
                <DxSorting mode="multiple" />
                <DxStateStoring :enabled="true" :filterValue="false" storage-key="CashFlowConturiBanci" />
                <DxColumnChooser :enabled="true" title="Selector coloane"></DxColumnChooser>
                <DxSelection mode="multiple" />
                <DxSearchPanel :visible="true" :width="240" placeholder="Cauta ..."></DxSearchPanel>
                <DxColumn data-field="BancaNume" caption="Bancă" :group-index="0" />
                <DxColumn data-field="ContNumePC" caption="Cont" />
                <DxColumn data-field="ContSimbolPC" caption="Simbol cont" />
                <DxColumn caption="Sold(valută)" :cellTemplate="applySuffixValuta" calculate-sort-value="Final" :allow-sorting="true" />
                <DxColumn caption="Sold(RON)" :cellTemplate="applySuffixRON" calculate-sort-value="FinalLei" :allow-sorting="true" />
                <DxSummary :calculate-custom-summary="calculateSelectedRow">
                  <DxTotalItem name="sumaFinalLei" show-in-column="Sold(RON)" summary-type="custom" :customize-text="formatSummaryText" />
                  <DxGroupItem name="sumaFinalLei" show-in-column="Sold(RON)" summary-type="custom" :customize-text="formatSummaryText" :alignByColumn="true" />
                </DxSummary>
                <DxZoomAndPan argument-axis="both" value-axis="both" />
              </DxDataGrid>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="bank-card" elevation="2">
            <v-card-title class="card-title">
              <v-icon color="error" size="24" class="mr-2">mdi-minus-circle</v-icon>
              <span>Credit</span>
            </v-card-title>
            <v-card-text class="pa-2">
              <DxDataGrid :allowColumnReordering="true" :data-source="conturiBanci.filter(cb => cb.Final < 0)"
                :allowColumnResizing="true" :columnAutoWidth="false" :columnMinWidth="50"
                :columnResizingMode="'nextColumn'" :show-borders="true" :syncLookupFilterValues="false"
                @exporting="onExporting" :filter-sync-enabled="true" style="height: 600px">
                <DxExportChart :enabled="true" :formats="['pdf', 'xlsx']" :allow-export-selected-data="true" />
                <DxFilterRow :visible="true" />
                <DxScrolling mode="virtual" />
                <DxGrouping :context-menu-enabled="false" :autoExpandAll="false" />
                <DxGroupPanel :visible="true" :allow-column-dragging="false" />
                <DxLoadPanel :enabled="true" :visible="loadingConturiBanci" />
                <DxSorting mode="multiple" />
                <DxStateStoring :enabled="true" :filterValue="false" storage-key="CashFlowConturiBanci" />
                <DxColumnChooser :enabled="true" title="Selector coloane"></DxColumnChooser>
                <DxSelection mode="multiple" />
                <DxSearchPanel :visible="true" :width="240" placeholder="Cauta ..."></DxSearchPanel>
                <DxColumn data-field="BancaNume" caption="Bancă" :group-index="0" />
                <DxColumn data-field="ContNumePC" caption="Cont" />
                <DxColumn data-field="ContSimbolPC" caption="Simbol cont" />
                <DxColumn caption="Sold(valută)" :cellTemplate="applySuffixValuta" calculate-sort-value="Final" :allow-sorting="true" />
                <DxColumn caption="Sold(RON)" :cellTemplate="applySuffixRON" calculate-sort-value="FinalLei" :allow-sorting="true" />
                <DxSummary :calculate-custom-summary="calculateSelectedRow">
                  <DxTotalItem name="sumaFinalLei" show-in-column="Sold(RON)" summary-type="custom" :customize-text="formatSummaryText" />
                  <DxGroupItem name="sumaFinalLei" show-in-column="Sold(RON)" summary-type="custom" :customize-text="formatSummaryText" :alignByColumn="true" />
                </DxSummary>
                <DxZoomAndPan argument-axis="both" value-axis="both" />
              </DxDataGrid>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>
<script lang="ts">
import { DxNumberBox } from 'devextreme-vue/number-box';
import DxDateRangeBox from 'devextreme-vue/date-range-box';
import DxButton from 'devextreme-vue/button';
import { DxItem, DxList } from 'devextreme-vue/list';
import { DxLoadIndicator } from 'devextreme-vue/load-indicator';
import {
    DxChart,
    DxCommonSeriesSettings,
    DxConstantLine,
    DxExport,
    DxLabel,
    DxLegend,
    DxSeries,
    DxTitle,
    DxTooltip,
    DxValueAxis,
    DxVisualRange,
    DxZoomAndPan
} from 'devextreme-vue/chart';

import {
    DxColumn,
    DxColumnChooser,
    DxDataGrid,
    DxExport as DxExportChart,
    DxFilterRow,
    DxGrouping,
    DxGroupItem,
    DxGroupPanel,
    DxLoadPanel,
    DxPaging,
    DxScrolling,
    DxSearchPanel,
    DxSelection,
    DxSorting,
    DxStateStoring,
    DxSummary,
    DxTexts,
    DxTotalItem
} from 'devextreme-vue/data-grid';
import { defineComponent } from 'vue'
import CashFlowDataService from "@/services/Rapoarte/CashFlowDataService";
import ResponseData from "@/types/responseData";
import DxTabPanel from 'devextreme-vue/tab-panel';

export default defineComponent({
    components: {
        DxZoomAndPan,
        DxNumberBox,
        DxDateRangeBox,
        DxLoadIndicator,
        DxChart,
        DxCommonSeriesSettings,
        DxExport,
        DxLegend,
        DxSeries,
        DxTitle,
        DxValueAxis,
        DxTooltip,
        DxConstantLine,
        DxLabel,
        DxVisualRange,
        DxExportChart,
        DxDataGrid,
        DxFilterRow,
        DxPaging,
        DxGrouping,
        DxTexts,
        DxGroupPanel,
        DxLoadPanel,
        DxSorting,
        DxStateStoring,
        DxColumnChooser,
        DxSelection,
        DxSearchPanel,
        DxColumn,
        DxScrolling,
        DxSummary,
        DxTotalItem,
        DxGroupItem,
        DxTabPanel,
        DxButton,
        DxList,
        DxItem
    },
    data() {
        return {
            // initialValues: [
            //     new Date(),
            //     (new Date()).setDate(new Date().getDate() + 30),
            // ],
            activeTab: 'predictie' as string,
            dataSource: [] as any[],
            topNeachitat: [] as any[],
            topNeincasat: [] as any[],
            loadingFacturi: true as Boolean,
            loadingConturiBanci: true as Boolean,
            modalFacturi: false as Boolean,
            totalDeIncasat: 0 as Number,
            totalDeAchitat: 0 as Number,
            totalSolduri: 0 as Number,
            conturiBanci: [] as any[],
            total: 0 as Number,
            days: 30 as Number,
            timeFrame: '1d' as string
        };
    },
    mounted() {
        this.getCashFlow(this.timeFrame, this.days);
    },
    methods: {
        getCashFlow(timeFrame, daysValue) {
            this.loadingFacturi = true;
            this.loadingConturiBanci = true;
            //let numberOfBackwardsDays = 0;
            let start = new Date();

            if (timeFrame === '1w') {
                start.setDate(start.getDate());
            } else if (timeFrame === '1m') {
                start.setMonth(start.getMonth() - 1);
            } else if (timeFrame === '6m') {
                start.setMonth(start.getMonth() - 6);
            } else if (timeFrame === '1y') {
                start.setMonth(start.getMonth() - 12);
            } else if (timeFrame === '5y') {
                start.setMonth(start.getMonth() - 5 * 12);
            } else if (timeFrame === 'max') {
                start = new Date(1990, 1, 1);
            }


            let end = new Date();
            end.setDate(end.getDate() + daysValue);
            CashFlowDataService.GetProcessedCashFlowData(start, end)
                .then((response: ResponseData) => {
                    const minus = response?.data?.Minus ?? [];
                    const plus = response?.data?.Plus ?? [];

                    // helper: parsează o dată ISO/T-like -> Date | null
                    const toDate = (s?: string | null): Date | null => {
                        if (!s) return null;
                        const d = new Date(s);
                        return isNaN(d.getTime()) ? null : d;
                    };

                    const s1Date = toDate(minus[0]?.Scadenta ?? null);
                    const s2Date = toDate(plus[0]?.Scadenta ?? null);

                    // dacă există cel puțin una, mută start la cea mai veche dintre ele;
                    // altfel, păstrează start neschimbat
                    if (s1Date || s2Date) {
                        const earliest = [s1Date, s2Date]
                            .filter((d): d is Date => d instanceof Date)
                            .sort((a, b) => a.getTime() - b.getTime())[0];
                        if (earliest) start = new Date(earliest);
                    }

                    // helper: format YYYY-MM-DD (fără locale)
                    const fmt = (d: Date) =>
                        `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate()
                            .toString()
                            .padStart(2, '0')}`;

                    // generează zilele dintre start și end (incluziv)
                    const dates: string[] = [];
                    const cur = new Date(start);
                    const endCopy = new Date(end);
                    while (cur <= endCopy) {
                        dates.push(fmt(cur));
                        cur.setDate(cur.getDate() + 1);
                    }

                    const getDaySum = (arr: any[], day: string) =>
                        arr
                            .filter(x => (x?.Scadenta ? (x.Scadenta as string).split('T')[0] === day : false))
                            .reduce((sum: number, a: any) => sum + (Number(a?.Suma) || 0), 0);

                    const getDayPartners = (arr: any[], day: string) =>
                        arr
                            .filter(x => (x?.Scadenta ? (x.Scadenta as string).split('T')[0] === day : false))
                            .reduce((set: any[], a: any) => set.concat(Array.isArray(a?.Parteneri) ? a.Parteneri : []), []);

                    this.dataSource = dates.map(day => ({
                        state: day,
                        minus: getDaySum(minus, day),
                        plus: getDaySum(plus, day),
                        minusParteneri: getDayPartners(minus, day),
                        plusParteneri: getDayPartners(plus, day),
                        total: 0,
                    }));

                    this.dataSource = this.dataSource.map(el => {
                        el.total = el.minus + el.plus;
                        return el;
                    });

                    this.totalDeAchitat = this.dataSource.reduce((acc, cur) => acc + cur.minus, 0);
                    this.totalDeIncasat = this.dataSource.reduce((acc, cur) => acc + cur.plus, 0);
                    this.total = this.dataSource.reduce((acc, cur) => acc + cur.total, 0);

                    // top neachitat
                    this.topNeachitat = [];
                    let topNeachitatCUIs: string[] = [];
                    for (const row of this.dataSource) {
                        for (const obj of row.minusParteneri ?? []) {
                            const cui = obj?.CUI;
                            const sold = Number(obj?.Sold) || 0;
                            if (!cui) continue;
                            if (topNeachitatCUIs.includes(cui)) {
                                this.topNeachitat.filter(tn => tn.CUI === cui)[0].Sold += sold;
                            } else {
                                this.topNeachitat.push({ Nume: obj?.Nume ?? '', CUI: cui, Sold: sold });
                                topNeachitatCUIs.push(cui);
                            }
                        }
                    }

                    // top neincasat
                    this.topNeincasat = [];
                    let topNeincasatCUIS: string[] = [];
                    for (const row of this.dataSource) {
                        for (const obj of row.plusParteneri ?? []) {
                            const cui = obj?.CUI;
                            const sold = Number(obj?.Sold) || 0;
                            if (!cui) continue;
                            if (topNeincasatCUIS.includes(cui)) {
                                this.topNeincasat.filter(tn => tn.CUI === cui)[0].Sold += sold;
                            } else {
                                this.topNeincasat.push({ Nume: obj?.Nume ?? '', CUI: cui, Sold: sold });
                                topNeincasatCUIS.push(cui);
                            }
                        }
                    }

                    const fmtRon = (val: number) => {
                        const parts = val.toFixed(2).split('.');
                        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                        return parts.join(',') + ' RON';
                    };

                    this.topNeachitat = this.topNeachitat
                        .sort((a, b) => a.Sold - b.Sold)
                        .map(tn => ({ Nume: tn.Nume, CUI: tn.CUI, Sold: fmtRon(tn.Sold) }));

                    this.topNeincasat = this.topNeincasat
                        .sort((a, b) => b.Sold - a.Sold)
                        .map(tn => ({ Nume: tn.Nume, CUI: tn.CUI, Sold: fmtRon(tn.Sold) }));
                })
                .catch((e: Error) => {
                    console.log(e);
                    // nu mai alertăm dacă listele sunt goale; alertăm doar pe erori reale (ex: 500, network)
                    alert("Eroare la aducerea datelor (rețea/server).");
                })
                .finally(() => {
                    this.loadingFacturi = false;
                });

            CashFlowDataService.GetDSH_BanciSolduri().then(response => {
                this.conturiBanci = response.data.filter(el => el.BancaID != null);
                this.totalSolduri = response.data.reduce((res, el) => res + el.FinalLei, 0);
            }).catch((e: Error) => {
                alert("Eroare la aducerea conturilor bancare!");
                console.log(e)
            }).finally(() => {
                this.loadingConturiBanci = false;
            });
        },
        customizeTooltip(info) {
            //console.log(info);
            if (info.seriesName === 'Total') {
                return {
                    text: info.argument + '\n' + 'Total: ' + info.value.toLocaleString('ro-RO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    }) + ' RON'
                }
            }

            return {
                text: (info.argument + '\n' + info.seriesName + ': ') +
                    `${info.value.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RON` +
                    `\nTotal: ${info.total.toLocaleString('ro-RO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    })} RON`,
            };
        },
        applySuffixValuta(cellElement, cellInfo) {
            let parts = cellInfo.data.Final.toFixed(2).split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            cellElement.textContent = parts.join(',') + ' ' + cellInfo.data.ValutaSimbol

        },
        applySuffixRON(cellElement, cellInfo) {
            let parts = cellInfo.data.FinalLei.toFixed(2).split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            cellElement.textContent = parts.join(',') + ' RON'
        },
        calculateSelectedRow(options) {

            if (options.name === 'sumaFinalLei') {
                if (options.summaryProcess === 'start') {
                    options.totalValue = 0;
                } else if (options.summaryProcess === 'calculate') {
                    options.symbol = 'RON';
                    options.totalValue += options.value.FinalLei;
                }
            }
        },
        formatSummaryText(e) {
            let parts = e.value.toFixed(2).split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            return 'Total: ' + parts.join(',') + ' RON';
        },
        updateRange(e) {
            this.days = e.value;
            this.getCashFlow(this.timeFrame, e.value);
        },
        updateTimeframe(timeFrame) {
            this.getCashFlow(timeFrame, this.days);
        }
    },
    watch: {
        timeFrame(newValue, oldValue) {
            this.updateTimeframe(newValue);
            //console.log(newValue, oldValue);
        }
    }
});
</script>
<style scoped>
.cashflow-container {
  padding: 2rem;
  background: linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%);
  min-height: 100vh;
}

/* ========== HEADER ========== */
.cashflow-header {
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.cashflow-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.25rem;
}

.cashflow-subtitle {
  font-size: 1rem;
  color: #718096;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

/* ========== TABS CARD ========== */
.tabs-card {
  border-radius: 16px !important;
  margin-bottom: 1.5rem;
  overflow: hidden;
}

/* ========== CONTROL CARD ========== */
.control-card {
  border-radius: 16px !important;
  margin-bottom: 1.5rem;
}

.control-label {
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 500;
  color: #1a202c;
}

/* ========== CHART CARD ========== */
.chart-card {
  border-radius: 16px !important;
  margin-bottom: 1.5rem;
}

.card-title {
  display: flex;
  align-items: center;
  font-weight: 600 !important;
  font-size: 1.125rem !important;
  padding: 1.5rem !important;
  border-bottom: 1px solid #e0e0e0;
}

/* ========== TIMEFRAME CARD ========== */
.timeframe-card {
  border-radius: 16px !important;
  margin-bottom: 1.5rem;
}

/* ========== STATS CARDS ========== */
.stats-row {
  margin-bottom: 1.5rem;
}

.stat-card {
  border-radius: 16px !important;
  padding: 1.5rem;
  display: flex;
  gap: 1.25rem;
  align-items: center;
  transition: all 0.3s ease;
  height: 100%;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12) !important;
}

.stat-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-card-plus .stat-icon-wrapper {
  background: rgba(67, 160, 71, 0.1);
}

.stat-card-minus .stat-icon-wrapper {
  background: rgba(211, 47, 47, 0.1);
}

.stat-card-total .stat-icon-wrapper {
  background: rgba(107, 113, 195, 0.1);
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 0.875rem;
  color: #718096;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  line-height: 1.2;
}

.plus_color {
  color: #43A047 !important;
}

.minus_color {
  color: #D32F2F !important;
}

/* ========== PARTNERS CARDS ========== */
.partners-row {
  margin-bottom: 1.5rem;
}

.partners-card {
  border-radius: 16px !important;
  height: 100%;
}

/* ========== BANKS CARDS ========== */
.banks-row {
  margin-bottom: 1.5rem;
}

.bank-card {
  border-radius: 16px !important;
  height: 100%;
}

/* ========== RESPONSIVE ========== */
@media (max-width: 960px) {
  .cashflow-container {
    padding: 1rem;
  }

  .cashflow-title {
    font-size: 1.5rem;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
  }

  .header-actions .v-btn {
    flex: 1;
  }

  .control-label {
    font-size: 0.875rem;
  }
}

@media (max-width: 600px) {
  .stat-card {
    flex-direction: column;
    text-align: center;
  }

  .stat-icon-wrapper {
    margin: 0 auto;
  }
}
</style>
